import { Header } from "@/components/layout/header";
import { Main } from "@/components/layout/main";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth";
import { LogOut, ChevronLeft, ChevronRight, Loader2, CheckCheck, Search } from "lucide-react";
import useManualLog from "@/hooks/manualLog";
import { ManualLogStatus } from "@/models/manualLog";
import { useState, useMemo, useCallback } from "react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import toastifyInstance from "@/utils/toastify";

const ManualLog: React.FC = () => {
    const logout = useAuthStore(state => state.logout);
    const { manualLogs, handleUpdateStatus, handleAcceptAllPending, isAcceptingAll, handleFindDuplicates, isFindingDuplicates, filters, setFilters, updateFiltersWithReset, isLoadingManualLogs: isloading } = useManualLog();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
    const [isAcceptAllDialogOpen, setIsAcceptAllDialogOpen] = useState(false);
    const [isDuplicatesDialogOpen, setIsDuplicatesDialogOpen] = useState(false);
    const [duplicatesData, setDuplicatesData] = useState<any>(null);

    const getStatusColor = useCallback((status: ManualLogStatus) => {
        switch (status) {
            case ManualLogStatus.Pending:
                return "bg-yellow-100 text-yellow-800";
            case ManualLogStatus.Approved:
                return "bg-green-100 text-green-800";
            case ManualLogStatus.Rejected:
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    }, []);

    const totalPages = useMemo(() => Math.ceil((manualLogs?.total || 0) / filters.limit), [manualLogs?.total, filters.limit]);

    const handlePageChange = useCallback((page: number) => {
        if (page < 1 || page > totalPages) return;
        setFilters({
            ...filters,
            page
        });
    }, [filters, totalPages, setFilters]);

    const handleLimitChange = useCallback((limit: number) => {
        setFilters({
            ...filters,
            limit,
            page: 1
        });
    }, [filters, setFilters]);

    const handleStatusUpdate = useCallback(async (manualLogId: string, status: ManualLogStatus) => {
        try {
            setIsProcessing(true);
            await handleUpdateStatus(manualLogId, status);
            toastifyInstance.success(status === ManualLogStatus.Approved ? 'Đã duyệt thành công' : 'Đã từ chối thành công');
            setIsDialogOpen(false);
        } catch (error) {
            toastifyInstance.error('Có lỗi xảy ra khi xử lý yêu cầu');
        } finally {
            setIsProcessing(false);
        }
    }, [handleUpdateStatus]);

    const handleDateChange = useCallback((type: 'start' | 'end', value: string) => {
        if (type === 'start') {
            if (value > filters.endDate) {
                updateFiltersWithReset({
                    startDate: value,
                    endDate: value
                });
            } else {
                updateFiltersWithReset({
                    startDate: value
                });
            }
        } else {
            if (value < filters.startDate) {
                updateFiltersWithReset({
                    startDate: value,
                    endDate: value
                });
            } else {
                updateFiltersWithReset({
                    endDate: value
                });
            }
        }
    }, [filters.startDate, filters.endDate, updateFiltersWithReset]);

    const handleStatusChange = useCallback((value: ManualLogStatus | "all") => {
        updateFiltersWithReset({
            status: value
        });
    }, [updateFiltersWithReset]);

    const renderPagination = useCallback(() => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, filters.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <Button
                    key={i}
                    variant={filters.page === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(i)}
                    className={`h-8 w-8 p-0 ${filters.page === i
                        ? "bg-blue-500 text-white hover:bg-blue-600"
                        : "border-gray-300 hover:bg-gray-50"
                        }`}
                >
                    {i}
                </Button>
            );
        }

        return (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">
                        Hiển thị {((filters.page - 1) * filters.limit) + 1} - {Math.min(filters.page * filters.limit, manualLogs?.total || 0)} / {manualLogs?.total || 0}
                    </span>
                    <Select
                        value={filters.limit.toString()}
                        onValueChange={(value) => handleLimitChange(Number(value))}
                    >
                        <SelectTrigger className="w-[100px] h-8 text-sm">
                            <SelectValue placeholder="Limit" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="10">10 / trang</SelectItem>
                            <SelectItem value="20">20 / trang</SelectItem>
                            <SelectItem value="50">50 / trang</SelectItem>
                            <SelectItem value="100">100 / trang</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center space-x-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(filters.page - 1)}
                        disabled={filters.page === 1 || isloading}
                        className="h-8 px-2 border-gray-300"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {startPage > 1 && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(1)}
                                disabled={isloading}
                                className="h-8 w-8 p-0 border-gray-300"
                            >
                                1
                            </Button>
                            {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
                        </>
                    )}
                    {pages}
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && <span className="px-2 text-gray-500">...</span>}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={isloading}
                                className="h-8 w-8 p-0 border-gray-300"
                            >
                                {totalPages}
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(filters.page + 1)}
                        disabled={filters.page === totalPages || isloading}
                        className="h-8 px-2 border-gray-300"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        );
    }, [filters.page, totalPages, filters.limit, manualLogs?.total, handlePageChange, handleLimitChange, isloading]);

    return (
        <>
            <Header fixed className='flex justify-between w-[inherit]'>
                <div className='mr-0 flex items-center justify-end space-x-4 w-[inherit]'>
                    <Button variant="outline" size="icon" onClick={logout}>
                        <LogOut />
                    </Button>
                </div>
            </Header>

            <Main>
                <div className='flex justify-between items-center mb-6'>
                    <h1 className='text-2xl font-bold text-gray-800'>Manual Log</h1>
                    <div className="flex gap-2">
                    <Button
                        variant="outline"
                        className="border-amber-500 text-amber-600 hover:bg-amber-50"
                        disabled={isFindingDuplicates || isloading}
                        onClick={async () => {
                            try {
                                const result = await handleFindDuplicates(filters.startDate, filters.endDate);
                                setDuplicatesData(result);
                                setIsDuplicatesDialogOpen(true);
                                if (result.total === 0) {
                                    toastifyInstance.success('Không có bản ghi trùng lặp');
                                }
                            } catch {
                                toastifyInstance.error('Có lỗi xảy ra khi tìm trùng lặp');
                            }
                        }}
                    >
                        {isFindingDuplicates ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                            <Search className="h-4 w-4 mr-2" />
                        )}
                        Tìm trùng lặp
                    </Button>
                    <Dialog open={isAcceptAllDialogOpen} onOpenChange={setIsAcceptAllDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                disabled={isAcceptingAll || isloading}
                            >
                                <CheckCheck className="h-4 w-4 mr-2" />
                                Duyệt tất cả
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white sm:max-w-[425px]">
                            <DialogHeader>
                                <DialogTitle className="text-xl font-semibold text-gray-800">
                                    Duyệt tất cả Manual Log
                                </DialogTitle>
                                <DialogDescription className="text-sm text-gray-500">
                                    Bạn có chắc chắn muốn duyệt tất cả các manual log đang chờ từ {filters.startDate} đến {filters.endDate}?
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter className="flex gap-3 pt-4">
                                <Button
                                    variant="outline"
                                    onClick={() => setIsAcceptAllDialogOpen(false)}
                                    disabled={isAcceptingAll}
                                >
                                    Hủy
                                </Button>
                                <Button
                                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                    disabled={isAcceptingAll}
                                    onClick={async () => {
                                        try {
                                            const result = await handleAcceptAllPending(filters.startDate, filters.endDate);
                                            toastifyInstance.success(`Đã duyệt ${result.accepted}/${result.total} manual log${result.failed > 0 ? `, ${result.failed} thất bại` : ''}`);
                                            setIsAcceptAllDialogOpen(false);
                                        } catch {
                                            toastifyInstance.error('Có lỗi xảy ra khi duyệt tất cả');
                                        }
                                    }}
                                >
                                    {isAcceptingAll ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                            Đang xử lý...
                                        </>
                                    ) : (
                                        'Xác nhận duyệt tất cả'
                                    )}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                    </div>
                </div>

                <Dialog open={isDuplicatesDialogOpen} onOpenChange={setIsDuplicatesDialogOpen}>
                    <DialogContent className="bg-white sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-semibold text-gray-800">
                                Kết quả tìm trùng lặp
                            </DialogTitle>
                            <DialogDescription className="text-sm text-gray-500">
                                Các bản ghi manual log cùng người dùng, cùng ngày ({filters.startDate} → {filters.endDate})
                            </DialogDescription>
                        </DialogHeader>
                        {duplicatesData?.total === 0 ? (
                            <p className="text-center text-gray-500 py-4">Không có bản ghi trùng lặp</p>
                        ) : (
                            <div className="space-y-4">
                                {duplicatesData?.duplicates?.map((dup: any, idx: number) => (
                                    <div key={idx} className="border rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <span className="font-semibold text-gray-800">{dup.userName}</span>
                                                <Badge className="bg-blue-100 text-blue-800">{dup.day}</Badge>
                                                <Badge className="bg-amber-100 text-amber-800">{dup.count} bản ghi</Badge>
                                            </div>
                                            <span className="text-sm text-gray-500">
                                                Tổng: {dup.totalMinutes} phút
                                            </span>
                                        </div>
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-gray-50">
                                                    <TableHead className="text-xs">Dự án</TableHead>
                                                    <TableHead className="text-xs">Công việc</TableHead>
                                                    <TableHead className="text-xs">Bắt đầu</TableHead>
                                                    <TableHead className="text-xs">Kết thúc</TableHead>
                                                    <TableHead className="text-xs">Phút</TableHead>
                                                    <TableHead className="text-xs">Trạng thái</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {dup.logs.map((log: any) => (
                                                    <TableRow key={log.manualLog._id}>
                                                        <TableCell className="text-xs">{log.project?.name}</TableCell>
                                                        <TableCell className="text-xs">{log.task?.name}</TableCell>
                                                        <TableCell className="text-xs">{format(new Date(log.manualLog.startTime), 'HH:mm')}</TableCell>
                                                        <TableCell className="text-xs">{format(new Date(log.manualLog.endTime), 'HH:mm')}</TableCell>
                                                        <TableCell className="text-xs">{log.manualLog.totalTime}</TableCell>
                                                        <TableCell>
                                                            <Badge className={`text-xs ${getStatusColor(log.manualLog.status)}`}>
                                                                {log.manualLog.status}
                                                            </Badge>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                ))}
                            </div>
                        )}
                    </DialogContent>
                </Dialog>

                <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Ngày bắt đầu</label>
                            <Input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleDateChange('start', e.target.value)}
                                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Ngày kết thúc</label>
                            <Input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleDateChange('end', e.target.value)}
                                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Trạng thái</label>
                            <Select
                                value={filters.status}
                                onValueChange={(value) => handleStatusChange(value as ManualLogStatus | "all")}
                                disabled={isloading}
                            >
                                <SelectTrigger className="border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                                    <SelectValue placeholder="Chọn trạng thái" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tất cả</SelectItem>
                                    <SelectItem value={ManualLogStatus.Pending}>Đang chờ</SelectItem>
                                    <SelectItem value={ManualLogStatus.Approved}>Đã duyệt</SelectItem>
                                    <SelectItem value={ManualLogStatus.Rejected}>Đã từ chối</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead className="font-semibold text-gray-700">Người dùng</TableHead>
                                    <TableHead className="font-semibold text-gray-700">Dự án</TableHead>
                                    <TableHead className="font-semibold text-gray-700">Công việc</TableHead>
                                    <TableHead className="font-semibold text-gray-700">Thời gian bắt đầu</TableHead>
                                    <TableHead className="font-semibold text-gray-700">Thời gian kết thúc</TableHead>
                                    <TableHead className="font-semibold text-gray-700">Ghi chú</TableHead>
                                    <TableHead className="font-semibold text-gray-700">Tổng thời gian</TableHead>
                                    <TableHead className="font-semibold text-gray-700">Trạng thái</TableHead>
                                    <TableHead className="font-semibold text-gray-700">Thao tác</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isloading ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center py-8">
                                            <div className="flex items-center justify-center">
                                                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                                                <span className="ml-2 text-gray-600">Đang tải...</span>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : manualLogs?.data?.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                                            Không có dữ liệu
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    manualLogs?.data?.map((log) => (
                                        <TableRow key={log.manualLog._id} className="hover:bg-gray-50">
                                            <TableCell className="font-medium">{log.user.name}</TableCell>
                                            <TableCell>{log.project.name}</TableCell>
                                            <TableCell>{log.task.name}</TableCell>
                                            <TableCell>{format(new Date(log.manualLog.startTime), 'dd/MM/yyyy HH:mm')}</TableCell>
                                            <TableCell>{format(new Date(log.manualLog.endTime), 'dd/MM/yyyy HH:mm')}</TableCell>
                                            <TableCell className="max-w-xs truncate">{log.manualLog.note}</TableCell>
                                            <TableCell>{log.manualLog.totalTime}</TableCell>
                                            <TableCell>
                                                <Badge className={getStatusColor(log.manualLog.status)}>
                                                    {log.manualLog.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {log.manualLog.status === ManualLogStatus.Pending && (
                                                    <Dialog open={isDialogOpen && selectedLogId === log.manualLog._id} onOpenChange={setIsDialogOpen}>
                                                        <DialogTrigger asChild>
                                                            <Button
                                                                variant="outline"
                                                                className="border-blue-500 text-blue-500 hover:bg-blue-50"
                                                                disabled={isProcessing}
                                                                onClick={() => setSelectedLogId(log.manualLog._id)}
                                                            >
                                                                {isProcessing ? (
                                                                    <>
                                                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                                        Đang xử lý...
                                                                    </>
                                                                ) : (
                                                                    'Xử lý'
                                                                )}
                                                            </Button>
                                                        </DialogTrigger>
                                                        <DialogContent className="bg-white sm:max-w-[425px]">
                                                            <DialogHeader className="space-y-4">
                                                                <DialogTitle className="text-xl font-semibold text-gray-800 text-center">
                                                                    Xử lý Manual Log
                                                                </DialogTitle>
                                                                <div className="text-sm text-gray-500 text-center">
                                                                    Bạn có chắc chắn muốn xử lý manual log này?
                                                                </div>
                                                            </DialogHeader>
                                                            <div className="flex gap-4 py-6">
                                                                <Button
                                                                    onClick={() => handleStatusUpdate(log.manualLog._id, ManualLogStatus.Approved)}
                                                                    disabled={isProcessing}
                                                                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-base font-medium transition-colors duration-200"
                                                                >
                                                                    {isProcessing ? (
                                                                        <>
                                                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                                            Đang xử lý...
                                                                        </>
                                                                    ) : (
                                                                        'Duyệt'
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    variant="destructive"
                                                                    onClick={() => handleStatusUpdate(log.manualLog._id, ManualLogStatus.Rejected)}
                                                                    disabled={isProcessing}
                                                                    className="w-full bg-rose-500 hover:bg-rose-600 text-white py-6 text-base font-medium transition-colors duration-200"
                                                                >
                                                                    {isProcessing ? (
                                                                        <>
                                                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                                            Đang xử lý...
                                                                        </>
                                                                    ) : (
                                                                        'Từ chối'
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </DialogContent>
                                                    </Dialog>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    {renderPagination()}
                </div>
            </Main>
        </>
    );
};

export default ManualLog;